import { TelegramCategoryParameterResponseDto, TelegramCountryResponseDto, telegramGeoFindAnyById } from '@/generated';
import {
  CategoryIdDict,
  CategoryNameDict,
  Dicts,
  ExtendedCategoryParamsDict,
  ExtentedCategoryParameters,
  GeoDict,
  GeoItem,
  GeoPieceInput,
  GeoType,
} from './types';
import { runtimeConfig } from '@/config/runtime-config';

const WL_COUNTRY = runtimeConfig.defaultCountry.normalizedName;

export const convertCategoryParametersToFlat = (
  tree: TelegramCategoryParameterResponseDto[],
): { dict: ExtendedCategoryParamsDict; flat: ExtentedCategoryParameters[] } => {
  const arrToConvert: any[] = [...tree];
  const resultArr: any[] = [];
  const dict: ExtendedCategoryParamsDict = {};

  while (true) {
    if (arrToConvert.length === 0) {
      break;
    }
    const elem = arrToConvert.shift();
    if (!elem) {
      break;
    }
    dict[elem.id] = elem;
    if (elem.children && elem.children.length > 0) {
      arrToConvert.push(...elem.children.map((el) => ({ ...el, parentId: elem.id })));
    }
    resultArr.push(elem);
  }

  return { dict, flat: resultArr };
};

export const getLightDicts = (allDicts: Dicts, geo: GeoItem, categories: string[]) => {
  const geoDict: GeoDict = {};
  if ('countryId' in geo && geo.countryId) {
    geoDict[geo.name] = allDicts.geoDict[geo.name];
  }

  if ('regionId' in geo && geo.regionId) {
    const region = allDicts.geoDict[geo.name];
    geoDict[geo.name] = allDicts.geoDict[geo.name];
    geoDict[region.pn!] = allDicts.geoDict[region.pn!];
  }

  if ('cityId' in geo && geo.cityId) {
    const city = allDicts.geoDict[geo.name];
    geoDict[geo.name] = allDicts.geoDict[geo.name];

    geoDict[city.pn!] = allDicts.geoDict[city.pn!];
    const region = allDicts.geoDict[city.pn!];
    geoDict[region.pn!] = allDicts.geoDict[region.pn!];
  }

  const categoryIdDict: CategoryIdDict = {};
  const categoryNameDict: CategoryNameDict = {};

  categories.forEach((cat: string) => {
    const foundCat = allDicts.categoryIdDict[cat];
    categoryIdDict[cat] = foundCat;
    categoryNameDict[foundCat.link] = [foundCat];
  });

  return { categoryIdDict, categoryNameDict, geoDict };
};

export const findArrValueIndex = (options: string[] | null, value: string) => {
  if (!options) {
    return -1;
  }
  const index = options.findIndex((val: string) => val === value);
  if (index !== -1) {
    return index;
  }

  return -1;
};

export const isObjValueExists = (options: string, value: string) => {
  return options === value;
};

export const recordToSearchParams = (record: Record<string, string | string[] | undefined>) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(record)) {
    if (value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
      continue;
    }
    searchParams.append(key, value);
  }
  return searchParams;
};

export const searchParamsToObject = (searchParams: URLSearchParams) => {
  const obj = {};

  const assignNestedValue = (target: any, key: string, value: string) => {
    const parts = key.match(/[^[\]]+/g) || [key];
    let current = target;

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;

      if (isLast) {
        if (current[part]) {
          current[part] = Array.isArray(current[part]) ? [...current[part], value] : [current[part], value];
          return;
        }

        current[part] = value;
        return;
      }

      if (!current[part] || Array.isArray(current[part]) || typeof current[part] !== 'object') {
        current[part] = {};
      }

      current = current[part];
    });
  };

  for (const [key, value] of searchParams.entries()) {
    if (key.includes('[')) {
      assignNestedValue(obj, key, value);
      continue;
    }

    if (obj[key]) {
      obj[key] = Array.isArray(obj[key]) ? [...obj[key], value] : [obj[key], value];
      continue;
    }

    obj[key] = value;
  }
  return obj;
};

type GeoPathParams = {
  countryId: string;
  regionId: string;
  cityId: string;
  existingGeo?: GeoItem;
  isWLMode?: boolean;
  updateGeo: (newPath: string) => void;
};

export async function createCorrectGeoPathFromData({
  countryId,
  regionId,
  cityId,
  existingGeo,
  isWLMode = false,
  updateGeo,
}: GeoPathParams) {
  const geoConverted: { countryId?: string; regionId?: string; cityId?: string } = existingGeo || {};

  if (isWLMode) {
    if (!WL_COUNTRY) {
      throw new Error('incorrect WL settings');
    }

    if (cityId && cityId !== geoConverted.cityId) {
      telegramGeoFindAnyById(cityId).then((res) => {
        if (res.country?.normalizedName !== WL_COUNTRY) {
          throw new Error('incorrect attempt');
        }
        updateGeo(`${res.city?.normalizedName}`);
      });
      return;
    }

    if (regionId && regionId !== geoConverted.regionId) {
      telegramGeoFindAnyById(regionId).then((res) => {
        if (res.country?.normalizedName !== WL_COUNTRY) {
          throw new Error('incorrect attempt');
        }

        updateGeo(`${res.region?.normalizedName}`);
      });
      return;
    }

    if (countryId && countryId !== geoConverted.countryId) {
      telegramGeoFindAnyById(countryId).then((res) => {
        if (res.country?.normalizedName !== WL_COUNTRY) {
          throw new Error('incorrect attempt');
        }
        updateGeo('');
      });
      return;
    }
  }

  if (cityId && cityId !== geoConverted.cityId) {
    telegramGeoFindAnyById(cityId).then((res) => {
      updateGeo(`${res.country?.normalizedName}/${res.city?.normalizedName}`);
    });
    return;
  }

  if (regionId && regionId !== geoConverted.regionId) {
    telegramGeoFindAnyById(regionId).then((res) => {
      updateGeo(`${res.country?.normalizedName}/${res.region?.normalizedName}`);
    });
    return;
  }

  if (countryId && countryId !== geoConverted.countryId) {
    telegramGeoFindAnyById(countryId).then((res) => {
      updateGeo(res.country?.normalizedName || '');
    });
    return;
  }
  updateGeo('');
  return '';
}

export function convertTreeToFlatDict<T extends { children: any[]; id: string }>(tree: T[]): { [key: string]: T } {
  const result: { [key: string]: T } = {};

  const arrToWork = [...tree];

  while (true) {
    const elem = arrToWork.shift();
    if (!elem) {
      break;
    }

    result[elem?.id] = { ...elem, children: undefined };
    if (elem.children) {
      arrToWork.push(...elem.children);
    }
  }

  return result;
}

export function buildCategoryNameDict<T extends { link: string }>(categoryIdDict: {
  [id: string]: T;
}): { [link: string]: T[] } {
  const categoryNameDict: { [link: string]: T[] } = {};
  for (const key in categoryIdDict) {
    const category = categoryIdDict[key];
    if (Array.isArray(categoryNameDict[category.link])) {
      categoryNameDict[category.link].push(category);
    } else {
      categoryNameDict[category.link] = [category];
    }
  }
  return categoryNameDict;
}

export function convertGeoTreeToFlatDicts(geoTree: TelegramCountryResponseDto[]) {
  const result: GeoDict = {};
  const idToNameMap: { [key: string]: string } = {};

  const arrToWork: GeoPieceInput[] = [...geoTree];

  while (true) {
    const elem = arrToWork.shift();
    if (elem?.normalizedName === '') {
      continue;
    }
    if (!elem) {
      break;
    }

    if ('regions' in elem && elem.regions) {
      idToNameMap[elem.id] = elem.normalizedName;
      result[elem.normalizedName] = { id: elem.id, t: GeoType.country, pn: '' };

      arrToWork.push(...elem.regions);
      continue;
    }

    if ('cities' in elem && elem.cities) {
      idToNameMap[elem.id] = elem.normalizedName;
      result[elem.normalizedName] = { id: elem.id, t: GeoType.region, pn: idToNameMap[elem.countryId] };
      arrToWork.push(...elem.cities);

      continue;
    }
    if ('regionId' in elem) {
      idToNameMap[elem.id] = elem.normalizedName;
      result[elem.normalizedName] = { id: elem.id, t: GeoType.city, pn: idToNameMap[elem.regionId] };
    }
  }

  return result;
}

export const appendCategoryParam = (
  target: Record<string, unknown>,
  paramId: string,
  value: string | string[] | { from?: number; to?: number },
) => {
  if (Array.isArray(value)) {
    target[`param[${paramId}]`] = value;
    return;
  }

  if (value !== undefined && typeof value === 'object') {
    if (value.from !== undefined) {
      target[`param[${paramId}][from]`] = value.from;
    }

    if (value.to !== undefined) {
      target[`param[${paramId}][to]`] = value.to;
    }

    return;
  }

  target[`param[${paramId}]`] = value;
};
